import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useEvent } from "../../contexts/EventContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useGroupNavigation } from "../../hooks/useGroupNavigation";
import {
  ClipboardTextIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  UsersThreeIcon,
  ConfettiIcon,
  PencilSimpleIcon,
  XIcon,
  CheckIcon,
} from "@phosphor-icons/react";

import MemberInvitationForm from "../groups/MemberInvitationForm";
import EventCreation from "../events/EventCreation";
import { SurpriseEventHelpModal } from "../common";

import { apiService } from "../../services/api";
import { GroupCacheService } from "../../services/groupCache";
import {
  Person,
  PersonFormData,
  Group,
  Event,
  EventFormData,
} from "../../types";
import "./admin.css";
import "./admin-management.css";
import "./event-management.css";
import "./admin-shared.css";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr";

type AdminTab = "members" | "events";

const AdminDashboard: React.FC = () => {
  const { currentGroupSlug, isAuthenticated, getUserMainPerson } = useAuth();
  const { refreshEvents } = useEvent();
  const location = useLocation();
  const navigate = useNavigate();
  const { getGroupPath } = useGroupNavigation();
  const [group, setGroup] = useState<
    (Group & { persons: Person[]; isAdmin: boolean }) | null
  >(null);
  const [members, setMembers] = useState<Person[]>([]);

  const [activeTab, setActiveTab] = useState<AdminTab>("members");

  // Handle hash-based navigation for tabs
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash === "events" || hash === "members") {
      setActiveTab(hash as AdminTab);
    }
  }, [location.hash]);

  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    navigate(getGroupPath(`/admin#${tab}`), { replace: true });
  };

  const handleCopyInviteLink = async () => {
    if (!group) return;

    const inviteLink = `${window.location.origin}/login?group=${group.slug}`;

    try {
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = inviteLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleStartEditingPersonName = (
    personId: number,
    currentName: string
  ) => {
    setEditingPersonId(personId);
    setEditingPersonName(currentName);
  };

  const handleCancelEditingPersonName = () => {
    setEditingPersonId(null);
    setEditingPersonName("");
  };

  const handleSavePersonName = async (personId: number) => {
    const trimmedName = editingPersonName.trim();
    if (!group || !trimmedName) {
      handleCancelEditingPersonName();
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      await apiService.api.put(`/groups/${group.slug}/members/${personId}`, {
        name: trimmedName,
      });

      // Update local state
      setMembers((prev: Person[]) =>
        prev.map((member) =>
          member.id === personId ? { ...member, name: trimmedName } : member
        )
      );

      setGroup((prev) =>
        prev
          ? {
              ...prev,
              persons: prev.persons.map((member) =>
                member.id === personId
                  ? { ...member, name: trimmedName }
                  : member
              ),
            }
          : prev
      );

      GroupCacheService.invalidateGroupCache(group.slug);

      handleCancelEditingPersonName();
    } catch (error: any) {
      console.error("Failed to update person name:", error);
      setError(
        error.response?.data?.message ||
          "Failed to update person name. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const [mainPerson, setMainPerson] = useState<Person | null>(null);
  const [adminEvents, setAdminEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);

  const [showDuplicateEventError, setShowDuplicateEventError] = useState(false);
  const [duplicateEventName, setDuplicateEventName] = useState("");
  const [showDuplicateMemberError, setShowDuplicateMemberError] =
    useState(false);
  const [duplicateMemberName, setDuplicateMemberName] = useState("");

  const [selectedEventForEdit, setSelectedEventForEdit] =
    useState<Event | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState<number | null>(null);
  const [editingPersonName, setEditingPersonName] = useState<string>("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [showEventNotificationModal, setShowEventNotificationModal] =
    useState(false);
  const [createdEventForNotification, setCreatedEventForNotification] =
    useState<Event | null>(null);
  const [showPersonInviteModal, setShowPersonInviteModal] = useState(false);
  const [addedPersonForInvite, setAddedPersonForInvite] =
    useState<Person | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showEditChildModal, setShowEditChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState<Person | null>(null);

  const membersWithEmail = useMemo(
    () =>
      members.filter((member) => member.user?.email && !member.parentPersonId),
    [members]
  );
  const canSendEventNotification = membersWithEmail.length > 0;

  // Check if current user is admin - updated for new admin system
  const isAdmin = mainPerson && mainPerson.isAdmin;

  useEffect(() => {
    if (isAuthenticated && currentGroupSlug) {
      loadGroupData();
    }
  }, [isAuthenticated, currentGroupSlug]);

  useEffect(() => {
    if (group && isAdmin) {
      loadAllEvents(); // Load all events for admin management
    }
  }, [group, isAdmin]);

  // Delay spinner to avoid glitch effect on fast loads
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const isInitialLoading = !isAuthenticated || !group || mainPerson === null;

    if (isInitialLoading) {
      // Show spinner after 500ms delay
      timeoutId = setTimeout(() => {
        setShowSpinner(true);
      }, 500);
    } else {
      // Hide spinner immediately when loading stops
      setShowSpinner(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isAuthenticated, group, mainPerson]);

  const loadGroupData = async () => {
    if (!currentGroupSlug) return;

    try {
      setIsLoading(true);
      setError("");

      // Load group with members
      const groupResponse = await apiService.getGroupDetails(currentGroupSlug);
      setGroup(groupResponse);
      setMembers(groupResponse.persons || []);

      // Get the main person for the current user
      const person = await getUserMainPerson();
      setMainPerson(person);
    } catch (error: any) {
      console.error("Failed to load group data:", error);
      setError("Failed to load group data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check if an event is past
  const isEventPast = (eventDate: string): boolean => {
    const today = new Date();
    const eventDateObj = new Date(eventDate);
    // Set time to start of day for comparison
    today.setHours(0, 0, 0, 0);
    eventDateObj.setHours(0, 0, 0, 0);
    return eventDateObj < today;
  };

  const loadAllEvents = async () => {
    if (!group) return;

    try {
      setIsLoadingEvents(true);
      // Load ALL events (no status filter) for admin management
      const eventsData = await apiService.getGroupEvents();
      // Sort events by date DESC (most recent first)
      const sortedEvents = eventsData.sort((a: any, b: any) => 
        new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
      );
      setAdminEvents(sortedEvents);
    } catch (error: any) {
      console.error("Failed to load events:", error);
      setError("Failed to load events. Please try again.");
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const handleAddMember = async (
    memberData: PersonFormData
  ): Promise<boolean> => {
    if (!group) return false;

    try {
      setIsLoading(true);
      setError("");

      // Chiamiamo l'API senza invio automatico email
      const response = await apiService.api.post(
        `/groups/${group.slug}/members`,
        { ...memberData, skipInviteEmail: true }
      );
      const newMember = response.data as Person;

      setMembers((prev: Person[]) => [...prev, newMember]);

      setGroup((prev) =>
        prev
          ? {
              ...prev,
              persons: [...(prev.persons || []), newMember],
            }
          : prev
      );

      GroupCacheService.invalidateGroupCache(group.slug);

      // Se ci sono eventi e la persona ha un'email, chiedi se mandare invito
      if (
        adminEvents.length > 0 &&
        memberData.email &&
        !memberData.parentPersonId
      ) {
        setAddedPersonForInvite(newMember);
        setShowPersonInviteModal(true);
      }

      return true; // Success
    } catch (error: any) {
      console.error("Failed to add member:", error);

      // Check if it's a duplicate name error (409 Conflict)
      if (error.response?.status === 409) {
        setDuplicateMemberName(memberData.name);
        setShowDuplicateMemberError(true);
        setTimeout(() => setShowDuplicateMemberError(false), 5000);
        return false; // Duplicate error - don't close modal
      } else {
        setError(
          error.response?.data?.message ||
            "Failed to add member. Please try again."
        );
        return false; // Other error - don't close modal
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMemberAndCloseModal = async (
    memberData: PersonFormData
  ): Promise<boolean> => {
    const success = await handleAddMember(memberData);
    if (success) {
      setShowAddMemberModal(false);
    }
    return success;
  };

  const handleAddMemberClick = () => {
    // Nuova logica: apri sempre direttamente il modal per aggiungere membri
    setShowAddMemberModal(true);
  };

  const handleEditChild = (child: Person) => {
    setEditingChild(child);
    setShowEditChildModal(true);
  };

  const handleUpdateChild = async (
    memberData: PersonFormData
  ): Promise<boolean> => {
    if (!group || !editingChild) return false;

    try {
      setIsLoading(true);
      setError("");

      // Prepara i dati per l'aggiornamento
      const updateData: any = {
        name: memberData.name,
      };

      // Se non è più un bambino, aggiungi email e rimuovi parent
      if (!memberData.parentPersonId) {
        updateData.email = memberData.email;
        updateData.parentPersonId = null;
      } else {
        // Se è ancora un bambino, aggiorna solo il parent
        updateData.parentPersonId = memberData.parentPersonId;
      }

      const response = await apiService.api.put(
        `/groups/${group.slug}/members/${editingChild.id}`,
        updateData
      );
      const updatedPerson = response.data as Person;

      // Aggiorna lo stato locale immediatamente
      setMembers((prev: Person[]) =>
        prev.map((member) =>
          member.id === editingChild.id ? updatedPerson : member
        )
      );

      setGroup((prev) =>
        prev
          ? {
              ...prev,
              persons: prev.persons.map((member) =>
                member.id === editingChild.id ? updatedPerson : member
              ),
            }
          : prev
      );

      // Invalida la cache e ricarica i dati del gruppo
      GroupCacheService.invalidateGroupCache(group.slug);
      await loadGroupData();

      setShowEditChildModal(false);
      setEditingChild(null);
      return true;
    } catch (error: any) {
      console.error("Failed to update child:", error);
      setError(
        error.response?.data?.message ||
          "Failed to update member. Please try again."
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEventNotificationModal = (event: Event) => {
    if (!canSendEventNotification) {
      setError("Non ci sono membri con un indirizzo email da notificare.");
      return;
    }
    setCreatedEventForNotification(event);
    setShowEventNotificationModal(true);
  };

  const handleSendEventNotification = async () => {
    if (!createdEventForNotification || !group) return;

    try {
      setIsLoading(true);
      await apiService.api.post(
        `/events/${createdEventForNotification.id}/notify-group`
      );
      setShowEventNotificationModal(false);
      setCreatedEventForNotification(null);
    } catch (error: any) {
      console.error("Failed to send event notification:", error);
      setError("Errore nell'invio della notifica. Riprova più tardi.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPersonInvite = async () => {
    if (!addedPersonForInvite || !group) return;

    try {
      setIsLoading(true);
      await apiService.api.post(
        `/groups/${group.slug}/members/${addedPersonForInvite.id}/send-invite`
      );
      setShowPersonInviteModal(false);
      setAddedPersonForInvite(null);
    } catch (error: any) {
      console.error("Failed to send person invite:", error);
      setError("Errore nell'invio dell'invito. Riprova più tardi.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async (personId: number) => {
    if (!group) return;

    const member = members.find((m) => m.id === personId);
    if (!member) return;

    const hasChildren = members.some((m) => m.parentPersonId === personId);
    const confirmMessage = hasChildren
      ? `Vuoi eliminare ${member.name}? Questa azione eliminerà anche tutti i suoi bambini associati e non può essere annullata.`
      : `Vuoi eliminare ${member.name} dal gruppo? Questa azione non può essere annullata.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      await apiService.api.delete(`/groups/${group.slug}/members/${personId}`);
      const shouldKeepMember = (member: Person) =>
        member.id !== personId && member.parentPersonId !== personId;

      setMembers((prev: Person[]) => prev.filter(shouldKeepMember));

      setGroup((prev) =>
        prev
          ? {
              ...prev,
              persons: prev.persons.filter(shouldKeepMember),
            }
          : prev
      );

      GroupCacheService.invalidateGroupCache(group.slug);
    } catch (error: any) {
      console.error("Failed to remove member:", error);
      setError(
        error.response?.data?.message ||
          "Failed to remove member. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAdminStatus = async (
    personId: number,
    isAdmin: boolean
  ) => {
    if (!group) return;

    const member = members.find((m) => m.id === personId);
    if (!member) return;

    const confirmMessage = isAdmin
      ? `Vuoi rendere ${member.name} amministratore del gruppo? Potrà gestire membri, eventi e impostazioni.`
      : `Vuoi rimuovere i privilegi di amministratore a ${member.name}? Non potrà più gestire il gruppo.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      await apiService.api.put(
        `/groups/${group.slug}/members/${personId}/admin`,
        { isAdmin }
      );

      // Update local state
      setMembers((prev: Person[]) =>
        prev.map((member) =>
          member.id === personId ? { ...member, isAdmin } : member
        )
      );

      setGroup((prev) =>
        prev
          ? {
              ...prev,
              persons: prev.persons.map((member) =>
                member.id === personId ? { ...member, isAdmin } : member
              ),
            }
          : prev
      );

      GroupCacheService.invalidateGroupCache(group.slug);

      // Reload group data to update admin status
      await loadGroupData();
    } catch (error: any) {
      console.error("Failed to update admin status:", error);
      setError(
        error.response?.data?.message ||
          "Failed to update admin status. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEvent = async (
    eventData: EventFormData & { selectedPersons?: number[] }
  ) => {
    if (!selectedEventForEdit || !mainPerson) return;

    try {
      setIsLoading(true);
      setError("");

      const updatePayload: any = {
        name: eventData.name,
        eventDate: new Date(
          eventData.eventDate + "T00:00:00.000Z"
        ).toISOString(),
        bookingStartAt: eventData.bookingStartAt
          ? eventData.bookingStartAt + ":00.000Z"
          : undefined,
        bookingEndAt: eventData.bookingEndAt
          ? eventData.bookingEndAt + ":00.000Z"
          : undefined,
      };

      // Add surprise mode flag if provided
      if (eventData.isSurprise !== undefined) {
        updatePayload.isSurprise = eventData.isSurprise;
      }

      await apiService.api.put(
        `/events/${selectedEventForEdit.id}`,
        updatePayload
      );

      // Update active persons for the event if provided
      if (eventData.selectedPersons && selectedEventForEdit) {
        try {
          // Get current active persons
          const currentActivePerson =
            selectedEventForEdit.lists
              ?.map((list) => list.person?.id)
              .filter((id): id is number => Boolean(id)) || [];

          // Deactivate persons that are no longer selected
          const toDeactivate = currentActivePerson.filter(
            (id) => !eventData.selectedPersons!.includes(id)
          );
          for (const personId of toDeactivate) {
            await apiService.deactivatePersonForEvent(
              selectedEventForEdit.id,
              personId,
              {}
            );
          }

          // Activate newly selected persons
          const toActivate = eventData.selectedPersons.filter(
            (id) => !currentActivePerson.includes(id)
          );
          for (const personId of toActivate) {
            await apiService.activatePersonForEvent(
              selectedEventForEdit.id,
              personId,
              {}
            );
          }
        } catch (activationError) {
          console.warn(
            "Some person activation/deactivation failed:",
            activationError
          );
        }
      }

      await loadAllEvents();
      // Refresh the event dropdown in navigation
      await refreshEvents(true);
      if (group) {
        GroupCacheService.invalidateGroupCache(group.slug);
      }
      setSelectedEventForEdit(null);
    } catch (error: any) {
      console.error("Failed to update event:", error);

      // Check if it's a duplicate name error (409 Conflict)
      if (error.response?.status === 409) {
        setDuplicateEventName(eventData.name);
        setShowDuplicateEventError(true);
        setTimeout(() => setShowDuplicateEventError(false), 5000);
      } else {
        setError(
          error.response?.data?.message ||
            "Failed to update event. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEvent = async (
    eventData: EventFormData & {
      activateAllLists?: boolean;
      selectedPersons?: number[];
    }
  ) => {
    if (!group || !mainPerson) return;

    try {
      setIsLoading(true);
      setError("");

      // Convert date to ISO string format
      const eventPayload: any = {
        name: eventData.name,
        eventDate: new Date(
          eventData.eventDate + "T00:00:00.000Z"
        ).toISOString(),
      };

      // Add optional booking dates if provided
      if (eventData.bookingStartAt) {
        eventPayload.bookingStartAt = eventData.bookingStartAt;
      }
      if (eventData.bookingEndAt) {
        eventPayload.bookingEndAt = eventData.bookingEndAt;
      }

      // Add surprise mode flag if provided
      if (eventData.isSurprise !== undefined) {
        eventPayload.isSurprise = eventData.isSurprise;
      }

      const response = await apiService.api.post("/events", eventPayload);
      const createdEvent = response.data;

      // Activate lists for selected persons
      if (
        eventData.selectedPersons &&
        eventData.selectedPersons.length > 0 &&
        mainPerson
      ) {
        try {
          const activationPromises = eventData.selectedPersons.map((personId) =>
            apiService.activatePersonForEvent(createdEvent.id, personId, {})
          );
          await Promise.all(activationPromises);
        } catch (activationError) {
          console.warn("Some list activations failed:", activationError);
          // Don't fail the whole process if activation fails
        }
      }

      // Attiva automaticamente l'evento appena creato
      try {
        await apiService.updateEventStatus(createdEvent.id, { status: "open" });
      } catch (statusError) {
        console.warn("Failed to activate event automatically:", statusError);
      }

      // Reload events
      await loadAllEvents();
      // Refresh the event dropdown in navigation
      await refreshEvents(true);
      GroupCacheService.invalidateGroupCache(group.slug);
      setShowCreateEventModal(false);

      // Mostra subito la modal di notifica per l'evento attivato
      if (canSendEventNotification) {
        setCreatedEventForNotification(createdEvent);
        setShowEventNotificationModal(true);
      }
    } catch (error: any) {
      console.error("Failed to create event:", error);

      // Check if it's a duplicate name error (409 Conflict)
      if (error.response?.status === 409) {
        setDuplicateEventName(eventData.name);
        setShowDuplicateEventError(true);
        setTimeout(() => setShowDuplicateEventError(false), 5000);
      } else {
        setError(
          error.response?.data?.message ||
            "Failed to create event. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while we're checking authentication and admin status
  const isInitialLoading = !isAuthenticated || !group || mainPerson === null;

  // Only show spinner after delay to avoid glitch effect
  if (isInitialLoading && showSpinner) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container"></div>
      </div>
    );
  }

  // Return early if still loading (before delay) - prevents flash
  if (isInitialLoading) {
    return <div className="admin-dashboard"></div>;
  }

  // Only show access denied after we've loaded the user data
  if (!isAdmin) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <h2>Admin Access Required</h2>
          <p>Only group administrators can access this page.</p>
        </div>
      </div>
    );
  }

  // Convert members for MemberInvitationForm
  const existingMembersForForm = members.map((member: Person) => ({
    id: member.id,
    name: member.name,
    email: `user-${member.userId}`, // This is a placeholder - we'd need user email from backend
    isChild: !!member.parentPersonId,
  }));

  return (
    <div className="admin-dashboard">
      {/* Header moderno con nome famiglia e link invito */}
      <div className="modern-page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>
              <UsersThreeIcon size={38} weight="fill" />
              {group.name}{" "}
            </h1>
            <p>Gestisci membri, eventi e impostazioni del gruppo</p>
            <button
              className="copy-invite-link-btn"
              onClick={handleCopyInviteLink}
              title="Copia link di invito per invitare nuovi membri"
            >
              {linkCopied ? (
                <>
                  <CheckCircleIcon size={16} weight="fill" />
                  Copiato!
                </>
              ) : (
                <>
                  <ClipboardTextIcon size={16} />
                  Copia link gruppo
                </>
              )}
            </button>
          </div>
          <div className="header-decoration">
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
          </div>
        </div>
      </div>

      {error && (
        <div className="modern-error-message">
          <div className="error-content">
            <WarningCircleIcon size={20} weight="fill" />
            <p>{error}</p>
            <button onClick={() => setError("")} className="error-dismiss">
              <XIcon size={20} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {showDuplicateEventError && (
        <div className="duplicate-event-banner">
          <div className="duplicate-event-message">
            <span>
              <strong>Nome già in uso!</strong> Esiste già un evento chiamato "
              {duplicateEventName}". Scegli un nome diverso.
            </span>
          </div>
        </div>
      )}

      {showDuplicateMemberError && (
        <div className="duplicate-member-banner">
          <div className="duplicate-member-message">
            <span>
              <strong>Nome già in uso!</strong> Esiste già un membro chiamato "
              {duplicateMemberName}". Scegli un nome diverso.
            </span>
          </div>
        </div>
      )}

      {/* Admin Management Tabs */}
      <div className="admin-management-container">
        <div className="admin-tabs">
          <button
            className={`admin-tab-button ${
              activeTab === "members" ? "active" : ""
            }`}
            onClick={() => handleTabChange("members")}
          >
            Membri e Amministratori
          </button>
          <button
            className={`admin-tab-button ${
              activeTab === "events" ? "active" : ""
            }`}
            onClick={() => handleTabChange("events")}
          >
            Eventi
          </button>
        </div>

        <div className="admin-tab-content">
          {activeTab === "members" && (
            <div className="members-management-section">
              <div className="section-header">
                <div>
                  <h3 className="section-title">Gestione Membri</h3>
                  <p className="section-description">
                    Aggiungi, rimuovi e gestisci i privilegi dei membri del
                    gruppo
                  </p>
                </div>

                <div className="section-header-actions">
                  <button
                    className="add-item-button"
                    onClick={handleAddMemberClick}
                    disabled={isLoading}
                  >
                    <PlusIcon
                      size={14}
                      weight="bold"
                      style={{ marginRight: "5px", marginBottom: "1px" }}
                    />
                    Aggiungi Membro
                  </button>
                </div>
              </div>

              {members.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <UsersThreeIcon size={48} weight="light" />
                  </div>
                  <h4>Nessun Membro</h4>
                  <p>
                    Non ci sono ancora membri nel gruppo. Aggiungi il primo
                    membro per iniziare!
                  </p>
                </div>
              ) : (
                <div className="items-list">
                  {/* Prima mostra gli adulti */}
                  {members
                    .filter((m) => !m.parentPersonId)
                    .map((person) => (
                      <div key={person.id} className="list-item member-item">
                        <div className="item-first-row">
                          <div className="item-title-section">
                            <div className="member-avatar">
                              {person.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="member-main-info">
                              <div className="member-name-row">
                                {editingPersonId === person.id ? (
                                  <div className="inline-edit-container">
                                    <input
                                      type="text"
                                      value={editingPersonName}
                                      onChange={(e) =>
                                        setEditingPersonName(e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          handleSavePersonName(person.id);
                                        } else if (e.key === "Escape") {
                                          handleCancelEditingPersonName();
                                        }
                                      }}
                                      className="inline-edit-input"
                                      autoFocus
                                      disabled={isLoading}
                                    />
                                    <div className="inline-edit-actions">
                                      <button
                                        className="inline-edit-cancel"
                                        onClick={handleCancelEditingPersonName}
                                        disabled={isLoading}
                                        title="Annulla"
                                      >
                                        <XIcon size={20} weight="bold" />
                                      </button>
                                      <button
                                        className="inline-edit-save"
                                        onClick={() =>
                                          handleSavePersonName(person.id)
                                        }
                                        disabled={
                                          isLoading || !editingPersonName.trim()
                                        }
                                        title="Salva"
                                      >
                                        <CheckIcon size={20} weight="bold" />
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <h3 className="item-title">
                                      {person.name}
                                    </h3>
                                    <button
                                      className="edit-name-button"
                                      onClick={() =>
                                        handleStartEditingPersonName(
                                          person.id,
                                          person.name
                                        )
                                      }
                                      title="Modifica nome"
                                      disabled={isLoading}
                                    >
                                      <PencilSimpleIcon
                                        size={16}
                                        weight="bold"
                                      />
                                    </button>
                                  </>
                                )}
                                <div className="member-badges">
                                  {person.isAdmin && (
                                    <span className="admin-badge">Admin</span>
                                  )}
                                </div>
                              </div>
                              <p className="member-email">
                                {person.user?.email || "Email non disponibile"}
                              </p>
                            </div>
                          </div>

                          <div className="item-actions">
                            {!person.isAdmin && (
                              <button
                                className="action-button success"
                                onClick={() =>
                                  handleToggleAdminStatus(person.id, true)
                                }
                                disabled={isLoading}
                                title="Rendi amministratore"
                              >
                                Rendi Admin
                              </button>
                            )}
                            {person.isAdmin &&
                              members.filter((m) => m.isAdmin).length > 1 && (
                                <button
                                  className="action-button warning"
                                  onClick={() =>
                                    handleToggleAdminStatus(person.id, false)
                                  }
                                  disabled={isLoading}
                                  title="Rimuovi privilegi admin"
                                >
                                  Rimuovi Admin
                                </button>
                              )}
                            <button
                              className="action-button danger"
                              onClick={() => handleRemoveMember(person.id)}
                              disabled={isLoading || person.userId === mainPerson?.userId}
                              title={person.userId === mainPerson?.userId ? "Non puoi rimuovere te stesso dal gruppo" : "Rimuovi membro"}
                            >
                              Elimina
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                  {/* Poi mostra i bambini */}
                  {members
                    .filter((m) => m.parentPersonId)
                    .map((child) => {
                      const parent = members.find(
                        (p) => p.id === child.parentPersonId
                      );
                      return (
                        <div
                          key={child.id}
                          className="list-item member-item child-item"
                        >
                          <div className="item-first-row">
                            <div className="item-title-section">
                              <div className="member-avatar child-avatar">
                                {child.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="member-main-info">
                                <div className="member-name-row">
                                  {editingPersonId === child.id ? (
                                    <div className="inline-edit-container">
                                      <input
                                        type="text"
                                        value={editingPersonName}
                                        onChange={(e) =>
                                          setEditingPersonName(e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            handleSavePersonName(child.id);
                                          } else if (e.key === "Escape") {
                                            handleCancelEditingPersonName();
                                          }
                                        }}
                                        className="inline-edit-input"
                                        autoFocus
                                        disabled={isLoading}
                                      />
                                      <div className="inline-edit-actions">
                                        <button
                                          className="inline-edit-cancel"
                                          onClick={
                                            handleCancelEditingPersonName
                                          }
                                          disabled={isLoading}
                                          title="Annulla"
                                        >
                                          <XIcon size={20} weight="bold" />
                                        </button>
                                        <button
                                          className="inline-edit-save"
                                          onClick={() =>
                                            handleSavePersonName(child.id)
                                          }
                                          disabled={
                                            isLoading ||
                                            !editingPersonName.trim()
                                          }
                                          title="Salva"
                                        >
                                          <CheckIcon size={20} weight="bold" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <h3 className="item-title">
                                        {child.name}
                                      </h3>
                                      <button
                                        className="edit-name-button"
                                        onClick={() =>
                                          handleStartEditingPersonName(
                                            child.id,
                                            child.name
                                          )
                                        }
                                        title="Modifica nome"
                                        disabled={isLoading}
                                      >
                                        <PencilSimpleIcon
                                          size={16}
                                          weight="bold"
                                        />
                                      </button>
                                    </>
                                  )}
                                  <span className="role-badge child">
                                    Bambino
                                  </span>
                                </div>
                                <p className="child-info">
                                  Genitore: {parent?.name || "Sconosciuto"}
                                </p>
                              </div>
                            </div>

                            <div className="item-actions">
                              <button
                                className="action-button primary"
                                onClick={() => handleEditChild(child)}
                                disabled={isLoading}
                                title="Cambia genitore o converti in adulto"
                              >
                                Cambia Genitore
                              </button>
                              <button
                                className="action-button danger"
                                onClick={() => handleRemoveMember(child.id)}
                                disabled={isLoading}
                                title="Elimina bambino"
                              >
                                Elimina
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {activeTab === "events" && (
            <div className="events-management-section">
              <div className="section-header">
                <div>
                  <h3 className="section-title">Gestione Eventi</h3>
                  <p className="section-description">
                    Crea e gestisci eventi per il tuo gruppo
                  </p>
                </div>
                {adminEvents.length > 0 && (
                  <button
                    className="create-event-button"
                    onClick={() => setShowCreateEventModal(true)}
                    disabled={isLoading || isLoadingEvents}
                  >
                    <PlusIcon
                      size={14}
                      weight="bold"
                      style={{ marginRight: "5px", marginBottom: "1px" }}
                    />
                    Crea Evento
                  </button>
                )}
              </div>

              {isLoadingEvents ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Caricamento eventi...</p>
                </div>
              ) : adminEvents.length > 0 ? (
                <div className="events-list">
                  {adminEvents.map((event) => {
                    return (
                      <div key={event.id} className="event-management-card">
                        <div className="event-card-header">
                          <div className="event-card-info">
                            <label
                              className="event-toggle-compact"
                              title={
                                isEventPast(event.eventDate)
                                  ? "Non è possibile modificare eventi passati"
                                  : event.status === "open"
                                  ? "Evento attivo"
                                  : "Evento disattivo"
                              }
                            >
                              <input
                                type="checkbox"
                                checked={event.status === "open"}
                                onChange={async (e) => {
                                  if (!mainPerson) return;
                                  const newStatus = e.target.checked
                                    ? "open"
                                    : "draft";

                                  // Chiedi conferma solo per la disattivazione
                                  if (newStatus === "draft") {
                                    const confirmMessage =
                                      "Vuoi disattivare questo evento? I membri non potranno più vederlo.";
                                    if (!window.confirm(confirmMessage)) {
                                      e.target.checked = !e.target.checked; // Reset toggle
                                      return;
                                    }
                                  }

                                  try {
                                    setIsLoading(true);
                                    await apiService.updateEventStatus(
                                      event.id,
                                      {
                                        status: newStatus,
                                      }
                                    );
                                    await loadAllEvents();
                                    // Refresh the event dropdown in navigation
                                    await refreshEvents(true);
                                    if (group) {
                                      GroupCacheService.invalidateGroupCache(
                                        group.slug
                                      );
                                    }

                                    // Se stiamo attivando l'evento, chiedi se mandare notifica
                                    if (newStatus === "open") {
                                      handleOpenEventNotificationModal(event);
                                    }
                                  } catch (error: any) {
                                    setError(
                                      error.response?.data?.message ||
                                        "Errore nell'aggiornamento dello stato"
                                    );
                                    e.target.checked = !e.target.checked; // Reset toggle on error
                                  } finally {
                                    setIsLoading(false);
                                  }
                                }}
                                disabled={isLoading || isEventPast(event.eventDate)}
                              />
                              <span className="toggle-slider-compact"></span>
                            </label>
                            <div
                              className={`event-card-details ${
                                event.status === "draft" ? "inactive" : ""
                              } ${isEventPast(event.eventDate) ? "past-event" : ""}`}
                            >
                              <h4>
                                {event.name}{" "}
                                <span className="event-lists-count">
                                  ({event.lists?.length || 0}{" "}
                                  {(event.lists?.length || 0) === 1
                                    ? "Persona"
                                    : "Persone"}
                                  )
                                </span>
                              </h4>
                              <p className="event-card-date">
                                {new Date(event.eventDate).toLocaleDateString(
                                  "it-IT",
                                  {
                                    weekday: "long",
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  }
                                )}
                              </p>
                              {isEventPast(event.eventDate) && (
                                <span className="past-event-badge">
                                  Evento passato
                                </span>
                              )}
                              {event.isSurprise && (
                                <button
                                  className="surprise-event-badge clickable"
                                  onClick={() => setShowHelpModal(true)}
                                  title="Clicca per sapere come funziona un evento a sorpresa"
                                >
                                  Evento a sorpresa
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="event-card-actions">
                            <button
                              className="event-action-button edit"
                              onClick={() => setSelectedEventForEdit(event)}
                            >
                              Modifica Evento
                            </button>
                            {(() => {
                              const isEventActive = event.status === "open";
                              const isPastEvent = isEventPast(event.eventDate);
                              const canNotifyEvent =
                                isEventActive && canSendEventNotification && !isPastEvent;
                              const buttonTitle = isPastEvent
                                ? "Non è possibile inviare notifiche per eventi passati"
                                : !isEventActive
                                ? "Attiva l'evento per inviare notifiche"
                                : !canSendEventNotification
                                ? "Nessun membro con email da notificare"
                                : "Invia una notifica ai membri";
                              return (
                                <button
                                  className="event-action-button notify"
                                  onClick={() =>
                                    handleOpenEventNotificationModal(event)
                                  }
                                  disabled={isLoading || !canNotifyEvent}
                                  title={buttonTitle}
                                >
                                  Invia notifica
                                </button>
                              );
                            })()}
                            <button
                              className="event-action-button danger"
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    `Vuoi eliminare l'evento "${event.name}"? Questa azione eliminerà anche tutte le liste e prenotazioni associate e non può essere annullata.`
                                  )
                                )
                                  return;

                                try {
                                  setIsLoading(true);
                                  await apiService.deleteEvent(event.id);
                                  await loadAllEvents();
                                  await refreshEvents(true);
                                  if (group) {
                                    GroupCacheService.invalidateGroupCache(
                                      group.slug
                                    );
                                  }
                                } catch (error: any) {
                                  setError(
                                    error.response?.data?.message ||
                                      "Errore nell'eliminazione dell'evento"
                                  );
                                } finally {
                                  setIsLoading(false);
                                }
                              }}
                              disabled={isLoading}
                            >
                              Elimina
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <h4>Nessun Evento</h4>
                  <p>
                    Non ci sono ancora eventi per questo gruppo. <br />
                    Crea il primo evento per iniziare!
                  </p>
                  <button
                    className="create-event-button"
                    onClick={() => setShowCreateEventModal(true)}
                    disabled={isLoading}
                  >
                    <ConfettiIcon size={16} weight="fill" />
                    Crea Primo Evento
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowAddMemberModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowAddMemberModal(false);
            }
          }}
        >
          <div
            className="modal-content"
            role="document"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Aggiungi Membro della Famiglia o Amico</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddMemberModal(false)}
              >
                ×
              </button>
            </div>
            <MemberInvitationForm
              onAddMember={handleAddMemberAndCloseModal}
              existingMembers={existingMembersForForm}
              isLoading={isLoading}
              error={error}
            />
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateEventModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowCreateEventModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowCreateEventModal(false);
            }
          }}
        >
          <div
            className="modal-content"
            role="document"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Crea Nuovo Evento</h3>
              <button
                className="modal-close"
                onClick={() => setShowCreateEventModal(false)}
              >
                ×
              </button>
            </div>
            <EventCreation
              groupId={group.id}
              persons={members}
              currentPersonId={mainPerson?.id}
              onEventCreated={handleCreateEvent}
              onCancel={() => setShowCreateEventModal(false)}
              isLoading={isLoading}
              error={error}
            />
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {selectedEventForEdit && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedEventForEdit(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setSelectedEventForEdit(null);
            }
          }}
        >
          <div
            className="modal-content"
            role="document"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Modifica Evento: {selectedEventForEdit.name}</h3>
              <button
                className="modal-close"
                onClick={() => setSelectedEventForEdit(null)}
              >
                ×
              </button>
            </div>
            <EventCreation
              groupId={group.id}
              persons={members}
              currentPersonId={mainPerson?.id}
              onEventCreated={handleEditEvent}
              onCancel={() => setSelectedEventForEdit(null)}
              isLoading={isLoading}
              error={error}
              initialData={{
                name: selectedEventForEdit.name,
                eventDate: selectedEventForEdit.eventDate.split("T")[0],
                bookingStartAt: selectedEventForEdit.bookingStartAt
                  ? selectedEventForEdit.bookingStartAt.slice(0, 16)
                  : "",
                bookingEndAt: selectedEventForEdit.bookingEndAt
                  ? selectedEventForEdit.bookingEndAt.slice(0, 16)
                  : "",
                isSurprise: selectedEventForEdit.isSurprise || false,
              }}
              isEditing={true}
              initialSelectedPersons={
                selectedEventForEdit.lists
                  ?.map((list) => list.person?.id)
                  .filter((id): id is number => Boolean(id)) || []
              }
            />
          </div>
        </div>
      )}

      {/* Event Notification Modal */}
      {showEventNotificationModal && createdEventForNotification && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowEventNotificationModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowEventNotificationModal(false);
            }
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Evento pubblicato! Lo diciamo al gruppo?</h3>
              <button
                className="modal-close"
                onClick={() => setShowEventNotificationModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Il tuo evento{" "}
                <strong>"{createdEventForNotification.name}"</strong> è ora
                attivo e visibile per tutti.
              </p>
              <p>
                Vuoi inviare una notifica email ai membri del gruppo per
                informarli del nuovo evento?
              </p>
              <div className="modal-actions">
                <button
                  className="action-button primary"
                  onClick={handleSendEventNotification}
                  disabled={isLoading}
                >
                  {isLoading ? "Inviando..." : "Sì, invia notifica"}
                </button>
                <button
                  className="action-button secondary"
                  onClick={() => {
                    setShowEventNotificationModal(false);
                    setCreatedEventForNotification(null);
                  }}
                  disabled={isLoading}
                >
                  No, grazie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Person Invite Modal */}
      {showPersonInviteModal && addedPersonForInvite && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowPersonInviteModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowPersonInviteModal(false);
            }
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Inviare invito a {addedPersonForInvite.name}?</h3>
              <button
                className="modal-close"
                onClick={() => setShowPersonInviteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Hai aggiunto <strong>{addedPersonForInvite.name}</strong> al
                gruppo.
              </p>
              <p>
                Ci sono già eventi attivi nel gruppo. Vuoi inviare un invito a
                questa persona per vedere gli eventi del gruppo?
              </p>
              <div className="modal-actions">
                <button
                  className="action-button primary"
                  onClick={handleSendPersonInvite}
                  disabled={isLoading}
                >
                  {isLoading ? "Inviando..." : "Sì, invia invito"}
                </button>
                <button
                  className="action-button secondary"
                  onClick={() => {
                    setShowPersonInviteModal(false);
                    setAddedPersonForInvite(null);
                  }}
                  disabled={isLoading}
                >
                  No, grazie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal di aiuto per eventi sorpresa */}
      <SurpriseEventHelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Edit Child Modal */}
      {showEditChildModal && editingChild && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setShowEditChildModal(false);
            setEditingChild(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowEditChildModal(false);
              setEditingChild(null);
            }
          }}
        >
          <div
            className="modal-content"
            role="document"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Modifica {editingChild.name}</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowEditChildModal(false);
                  setEditingChild(null);
                }}
              >
                ×
              </button>
            </div>
            <MemberInvitationForm
              onAddMember={handleUpdateChild}
              existingMembers={existingMembersForForm.filter(
                (m) => m.id !== editingChild.id
              )}
              isLoading={isLoading}
              error={error}
              isEditing={true}
              initialData={{
                name: editingChild.name,
                email: editingChild.user?.email || "",
                parentPersonId: editingChild.parentPersonId || undefined,
                isChild: !!editingChild.parentPersonId,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
